import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Stack } from '@mui/material';

import {
  createNotificationProvider,
  deleteNotificationProvider,
  listNotificationProviders,
  testNotificationProviderConnection,
  updateNotificationProvider,
} from '../../api/notificationSettingsApi';
import { settingsKeys, useNotificationSettings, useUpdateNotificationSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { NotificationChannel } from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  MultiToggleField,
  ProviderManager,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';
import { NOTIFICATION_CHANNEL_OPTIONS } from '../options';

/** Notification channels, templates, opt-in and providers. */
export function NotificationSettingsPage() {
  const { canView, canManage, canManageCredentials } = useDomainPermissions('notifications');
  const query = useNotificationSettings();
  const update = useUpdateNotificationSettings();
  const form = useSettingsForm(query.data);
  const { feedback, notify, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Notification settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Notification settings"
      subtitle="Channels, templates, opt-in rules and notification providers."
      icon={<NotificationsRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Channels & rules" icon={<TuneRoundedIcon />}>
              <MultiToggleField
                label="Enabled channels"
                value={draft.enabledChannels}
                options={NOTIFICATION_CHANNEL_OPTIONS}
                onChange={(value) =>
                  form.setField('enabledChannels', value as NotificationChannel[])
                }
                disabled={disabled}
              />
              <SwitchField
                label="Order templates"
                description="Send transactional order notifications."
                checked={draft.orderTemplatesEnabled}
                onChange={(value) => form.setField('orderTemplatesEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Marketing notifications"
                checked={draft.marketingEnabled}
                onChange={(value) => form.setField('marketingEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Opt-in required"
                description="Require explicit customer opt-in before sending."
                checked={draft.optInRequired}
                onChange={(value) => form.setField('optInRequired', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <ProviderManager
              title="Notification providers"
              description="Email, SMS and push providers with their credentials."
              icon={<NotificationsRoundedIcon />}
              listKey={settingsKeys.notificationProviders}
              api={{
                list: listNotificationProviders,
                create: createNotificationProvider,
                update: updateNotificationProvider,
                remove: deleteNotificationProvider,
                test: testNotificationProviderConnection,
              }}
              canManage={canManage}
              canManageCredentials={canManageCredentials}
              credentialHint="Provider API key"
              onFeedback={notify}
            />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
